import React, { useState, useEffect, useRef } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/api';
import { ReactComponent as HorseHeadIcon } from '../../assets/icons/horse-head.svg';

const Icon = ({ d }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round" width="22" height="22">
    {Array.isArray(d) ? d.map((p, i) => <path key={i} d={p} />) : <path d={d} />}
  </svg>
);

const icons = {
  home:     <Icon d={['M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z','M9 22V12h6v10']} />,
  barn:     <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="22" height="22">
    <path d="M3 21V10l9-7 9 7v11" />
    <path d="M1 21h22" />
    <path d="M8 21v-8h8v8" />
    <path d="m8 13 8 8" />
    <path d="m16 13-8 8" />
    <path d="M10 8h4" />
  </svg>,
  horses:   <HorseHeadIcon width="24" height="24" aria-hidden="true" />,
  calendar: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="22" height="22">
    <rect x="3" y="5" width="18" height="16" rx="2" />
    <path d="M16 3v4" />
    <path d="M8 3v4" />
    <path d="M3 10h18" />
    <path d="M8 14h.01" />
    <path d="M12 14h.01" />
    <path d="M16 14h.01" />
    <path d="M8 18h.01" />
    <path d="M12 18h.01" />
    <path d="M16 18h.01" />
  </svg>,
  messages: <Icon d={['M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z']} />,
  shop:     <Icon d={['M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z','M3 6h18','M16 10a4 4 0 0 1-8 0']} />,
  more:     <Icon d={['M5 12h.01','M12 12h.01','M19 12h.01']} />,
  docs:     <Icon d={['M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z','M14 2v6h6','M16 13H8','M16 17H8','M10 9H8']} />,
  contacts: <Icon d={['M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2','M23 21v-2a4 4 0 0 0-3-3.87','M16 3.13a4 4 0 0 1 0 7.75']} />,
  weather:  <Icon d={['M18 10h-1.26A8 8 0 1 0 9 20h9a5 5 0 0 0 0-10z']} />,
  shows:    <Icon d={['M8 6h13','M8 12h13','M8 18h13','M3 6h.01','M3 12h.01','M3 18h.01']} />,
  reminders:<Icon d={['M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9','M13.73 21a2 2 0 0 1-3.46 0']} />,
  profile:  <Icon d={['M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2','M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z']} />,
  orgs:     <Icon d={['M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z']} />,
};

const MoreSheet = ({ isBarnUser, onClose }) => {
  const navigate = useNavigate();
  const ref = useRef();

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);

  const go = (path) => { navigate(path); onClose(); };

  const barnMore = [
    { path: '/community',    icon: icons.messages,  label: 'Messages' },
    { path: '/documents',    icon: icons.docs,      label: 'Documents' },
    { path: '/contacts',     icon: icons.contacts,  label: 'Contacts' },
    { path: '/reminders',    icon: icons.reminders, label: 'Reminders' },
    { path: '/shop',         icon: icons.shop,      label: 'Shop' },
    { path: '/shows',        icon: icons.shows,     label: 'Shows' },
    { path: '/weather-watch',icon: icons.weather,   label: 'Weather' },
    { path: '/profile',      icon: icons.profile,   label: 'Profile' },
  ];

  const ownerMore = [
    { path: '/community',    icon: icons.messages,  label: 'Messages' },
    { path: '/documents',    icon: icons.docs,      label: 'Documents' },
    { path: '/contacts',     icon: icons.contacts,  label: 'Contacts' },
    { path: '/reminders',    icon: icons.reminders, label: 'Reminders' },
    { path: '/shows',        icon: icons.shows,     label: 'Shows' },
    { path: '/weather-watch',icon: icons.weather,   label: 'Weather' },
    { path: '/organizations',icon: icons.orgs,      label: 'Organizations' },
    { path: '/profile',      icon: icons.profile,   label: 'Profile' },
  ];

  const items = isBarnUser ? barnMore : ownerMore;

  return (
    <>
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 999 }} onClick={onClose} />
      <div ref={ref} style={{
        position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 1000,
        background: '#fff', borderRadius: '20px 20px 0 0',
        padding: '12px 0 32px', boxShadow: '0 -4px 24px rgba(0,0,0,0.12)',
      }}>
        <div style={{ width: 40, height: 4, background: '#ddd', borderRadius: 2, margin: '0 auto 16px' }} />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '4px 0', padding: '0 8px' }}>
          {items.map(item => (
            <button key={item.path} onClick={() => go(item.path)} style={{
              display: 'flex', flexDirection: 'column', alignItems: 'center',
              gap: 6, padding: '14px 8px', background: 'none', border: 'none',
              cursor: 'pointer', borderRadius: 12, color: 'var(--text-secondary)',
              fontSize: 11, fontWeight: 500,
            }}>
              <span style={{ color: 'var(--cr-primary)' }}>{item.icon}</span>
              {item.label}
            </button>
          ))}
        </div>
      </div>
    </>
  );
};

const BARN_ROLES = new Set(['staff', 'manager', 'trainer']);

const BottomNav = () => {
  const [isBarnUser, setIsBarnUser] = useState(false);
  const [showMore, setShowMore]     = useState(false);
  const [resolved, setResolved]     = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    api.get('/organizations/mine').then(({ data }) => {
      const hasBarnRole = (data || []).some(o => o.my_status === 'active');
      setIsBarnUser(hasBarnRole);
    }).catch(() => {}).finally(() => setResolved(true));
  }, [user && user.id]);

  if (!resolved) return null;

  const navItem = (to, icon, label, end = false) => (
    <NavLink to={to} end={end}
      className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`}>
      {icon}
      <span>{label}</span>
    </NavLink>
  );

  return (
    <>
      {showMore && <MoreSheet isBarnUser={isBarnUser} onClose={() => setShowMore(false)} />}
      <nav className="bottom-nav">
        {navItem('/', icons.home, 'Home', true)}
        {isBarnUser && navItem('/staff', icons.barn, 'Barn')}
        {navItem('/horses', icons.horses, 'Horses')}
        {navItem('/calendar', icons.calendar, 'Calendar')}
        {!isBarnUser && navItem('/shop', icons.shop, 'Shop')}
        <button className="nav-item" onClick={() => setShowMore(s => !s)}
          style={{ background: 'none', border: 'none', cursor: 'pointer', width: '100%' }}>
          {icons.more}
          <span>More</span>
        </button>
      </nav>
    </>
  );
};

export default BottomNav;